export interface PartnerApp {
  name: string
  description: string
  longDescription: string
  url: string
  icon: string
  actionLabel?: string
}

export const partnerApps: Record<string, PartnerApp> = {
  'glow-loyalty': {
    name: 'Glow Loyalty',
    icon: '/images/partners/glow-loyalty.png',
    url: 'https://apps.shopify.com/glow-loyalty',
    description:
      'Use PayWhirl subscriptions with points and VIP rewards to give subscribers a reason to stay longer and buy more often.',
    longDescription:
      'Glow Loyalty helps you pair PayWhirl subscriptions with points, rewards, and VIP perks. Together, they create a more compelling subscription experience that can improve retention, increase repeat purchases, and strengthen customer lifetime value.',
  },
  'upsell-wizard': {
    name: 'Upsell Wizard',
    icon: '/images/partners/upsell-wizard.jpg',
    url: 'https://apps.shopify.com/upsell-wizard',
    description:
      'Pair subscription offers with pre- and post-purchase upsells to lift average order value around every PayWhirl checkout.',
    longDescription:
      'Upsell Wizard helps you add pre-purchase and post-purchase offers around your PayWhirl subscription flow. It is a strong fit for merchants who want to increase average order value while keeping recurring billing and plan management inside PayWhirl.',
  },
  klaviyo: {
    name: 'Klaviyo: Email Marketing & SMS',
    icon: '/images/partners/klaviyo.png',
    url: 'https://apps.shopify.com/klaviyo-email-marketing',
    description:
      'Turn PayWhirl subscriber events into smarter email and SMS flows for onboarding, renewal reminders, and churn prevention.',
    longDescription:
      'Klaviyo helps you turn PayWhirl subscription activity into timely email and SMS campaigns. Use it to welcome new subscribers, remind customers about upcoming renewals, reduce churn, and keep subscription revenue growing over time.',
  },
  pagefly: {
    name: 'PageFly Landing Page Builder',
    icon: '/images/partners/pagefly.png',
    url: 'https://pagefly.io/?ref=paywhirl&target=app-listing',
    description:
      'Build custom landing and product pages that sell PayWhirl subscription plans more clearly and convert better.',
    longDescription:
      'PageFly gives you more control over how PayWhirl subscriptions are presented across product pages, landing pages, and campaign experiences. It is especially useful when you want to explain subscription value more clearly and improve conversion with custom layouts.',
  },
  storetasker: {
    name: 'Storetasker',
    icon: '/images/partners/storetasker.png',
    url: 'https://www.storetasker.com/partners/paywhirl?ref=paywhirl',
    actionLabel: 'Learn More',
    description:
      'Bring in vetted Shopify specialists to customize PayWhirl, handle technical changes quickly, and keep projects moving without hiring full-time.',
    longDescription:
      'Storetasker gives you access to experienced Shopify specialists who can help with design, development, marketing, and technical setup. For PayWhirl merchants, it is a practical option when you need fast support for subscription updates, storefront improvements, or more advanced customizations without building a full in-house team.',
  },
  brightside: {
    name: 'Brightside Marketing and Design',
    icon: '/images/partners/brightside.png',
    url: 'https://www.ilovebrightside.studio/ebizselect-paywhirl',
    actionLabel: 'Learn More',
    description:
      'Use a PayWhirl-savvy agency to make subscription updates easier, simplify complex setups, and polish the customer experience around recurring revenue.',
    longDescription:
      'Brightside is a strong fit for brands that want hands-on PayWhirl expertise and thoughtful execution. They can help simplify complex Shopify setups, improve the subscription experience, and support broader marketing work that helps recurring revenue grow.',
  },
  'shopify-experts': {
    name: 'Shopify Experts',
    icon: '/images/partners/shopify-experts.png',
    url: 'https://www.shopify.com/partners/directory',
    actionLabel: 'Learn More',
    description:
      'Find developers, designers, and agencies who can turn PayWhirl requirements into custom Shopify builds, theme work, and app integrations.',
    longDescription:
      'Shopify Experts makes it easier to find developers, designers, and agencies for custom Shopify work. If your PayWhirl setup needs tailored subscription flows, theme changes, or deeper integrations, this directory gives you a place to find the right partner.',
  },
  'phoenix-industries': {
    name: 'Phoenix Industries Inc.',
    icon: '/images/partners/phoenix-industries.png',
    url: 'http://phoenixindustriesinc.com/?ref=paywhirl',
    actionLabel: 'Learn More',
    description:
      'Pair PayWhirl with a digital marketing partner that can shape the broader strategy around acquisition, retention, and business-specific growth goals.',
    longDescription:
      'Phoenix Industries Inc. focuses on digital marketing strategies tailored to each business. For PayWhirl merchants, that can be especially useful when the subscription setup is already in place and the next step is improving acquisition, retention, and the broader growth strategy around recurring revenue.',
  },
  givy: {
    name: 'Givy - Gifting & Gift Cards',
    icon: '/images/partners/givy.png',
    url: 'https://apps.shopify.com/givy',
    description:
      'Make it easier to gift subscriptions or store credit so PayWhirl can support more acquisition and seasonal campaigns.',
    longDescription:
      'Givy helps you add gifting and gift card experiences around PayWhirl subscriptions. It is a good fit for merchants who want to offer giftable memberships, seasonal campaigns, or store credit options that support subscriber acquisition.',
  },
  'smile-io': {
    name: 'Smile: Loyalty Program Rewards',
    icon: '/images/partners/smile-io.png',
    url: 'https://apps.shopify.com/smile-io',
    description:
      'Add points, referrals, and VIP benefits on top of PayWhirl plans to make subscriptions feel more rewarding.',
    longDescription:
      'Smile helps you add points, referrals, and VIP benefits to the subscription experience. Used with PayWhirl, it can make subscriptions feel more valuable and give customers stronger reasons to stay engaged over time.',
  },
  'product-reviews-addon': {
    name: 'Stamped Reviews & Loyalty',
    icon: '/images/partners/product-reviews-addon.png',
    url: 'https://apps.shopify.com/product-reviews-addon',
    description:
      'Use reviews and loyalty together to increase trust and give shoppers stronger reasons to subscribe with PayWhirl.',
    longDescription:
      'Stamped combines social proof with loyalty features, which can be especially effective for subscription brands. When paired with PayWhirl, it helps build trust, increase conversion, and encourage customers to stay subscribed.',
  },
  loyaltylion: {
    name: 'LoyaltyLion: Rewards & Loyalty',
    icon: '/images/partners/loyaltylion.png',
    url: 'https://apps.shopify.com/loyaltylion',
    description:
      'Turn PayWhirl subscriptions into a loyalty engine by rewarding long-term customers and repeat engagement.',
    longDescription:
      'LoyaltyLion helps you reward long-term customers with points, referrals, and loyalty incentives. Paired with PayWhirl, it can make subscription retention feel more intentional and give repeat customers more reasons to stay active.',
  },
  growave: {
    name: 'Growave: Loyalty & Wishlist',
    icon: '/images/partners/growave.png',
    url: 'https://apps.shopify.com/growave',
    description:
      'Combine subscriptions with loyalty, wishlists, and referrals to improve retention and repeat purchase behavior.',
    longDescription:
      'Growave brings together loyalty, wishlists, referrals, and reviews in one place. Used with PayWhirl, it can help you create a richer customer experience that supports retention and repeat purchasing.',
  },
  govalo: {
    name: 'Govalo Gift Card Suite',
    icon: '/images/partners/govalo.png',
    url: 'https://apps.shopify.com/govalo',
    description:
      'Support giftable subscription experiences and store credit programs that expand how shoppers buy with PayWhirl.',
    longDescription:
      'Govalo helps you offer gift cards, giftable products, and store credit experiences that work well alongside subscriptions. For PayWhirl merchants, that opens up more flexible ways for customers to buy, gift, and engage with recurring offers.',
  },
  omnisend: {
    name: 'Omnisend Email Marketing & SMS',
    icon: '/images/partners/omnisend.png',
    url: 'https://apps.shopify.com/omnisend',
    description:
      'Use PayWhirl subscription behavior to trigger campaigns that win new subscribers and keep existing ones engaged.',
    longDescription:
      'Omnisend gives you email and SMS tools for welcome flows, renewal reminders, and re-engagement campaigns. Used with PayWhirl, it can help you win new subscribers and keep existing ones engaged over time.',
  },
  mailchimp: {
    name: 'Mailchimp Email SMS Marketing',
    icon: '/images/partners/mailchimp.png',
    url: 'https://apps.shopify.com/mailchimp',
    description:
      'Sync PayWhirl customer data into automation flows that recover churn risk and nurture repeat subscription revenue.',
    longDescription:
      'Mailchimp helps you turn customer and order data into automated campaigns across email and SMS. When used with PayWhirl, it is a practical way to support onboarding, retention, and repeat subscription revenue through broader lifecycle marketing.',
  },
  attentive: {
    name: 'Attentive: AI-led Email & SMS',
    icon: '/images/partners/attentive.png',
    url: 'https://apps.shopify.com/attentive',
    description:
      'Reach subscribers with high-intent email and SMS journeys tied to renewals, replenishment, and save offers.',
    longDescription:
      'Attentive is well suited for high-intent lifecycle messaging across email and SMS. Paired with PayWhirl, it can support renewal reminders, replenishment campaigns, and save offers that help protect recurring revenue.',
  },
  'postscript-sms-marketing': {
    name: 'Postscript SMS Marketing',
    icon: '/images/partners/postscript-sms-marketing.jpeg',
    url: 'https://apps.shopify.com/postscript-sms-marketing',
    description:
      'Build subscriber-focused SMS flows around cart recovery, renewals, and win-back moments tied to PayWhirl events.',
    longDescription:
      'Postscript helps you build SMS campaigns and automations around key subscription moments. With PayWhirl, it can support abandoned-cart recovery, renewal communication, and win-back messaging that keeps subscribers active.',
  },
  marsello: {
    name: 'Marsello: Loyalty, Email, SMS',
    icon: '/images/partners/marsello.png',
    url: 'https://apps.shopify.com/marsello',
    description:
      'Combine PayWhirl recurring revenue with loyalty and lifecycle marketing to improve retention over time.',
    longDescription:
      'Marsello combines loyalty with email and SMS marketing, which makes it a strong option for subscription brands. Used with PayWhirl, it can help you connect recurring revenue to reward-driven campaigns and retention messaging.',
  },
  recart: {
    name: 'Recart: SMS & List Growth',
    icon: '/images/partners/recart.png',
    url: 'https://apps.shopify.com/recart',
    description:
      'Capture more subscriber intent with SMS list growth and cart recovery flows that support PayWhirl conversion.',
    longDescription:
      'Recart focuses on list growth, SMS campaigns, and cart recovery. Alongside PayWhirl, it can help you capture more subscription intent before checkout and bring customers back when they drop off.',
  },
  postpilot: {
    name: 'PostPilot Direct Mail Platform',
    icon: '/images/partners/postpilot.jpeg',
    url: 'https://apps.shopify.com/postpilot',
    description:
      'Add direct mail to PayWhirl retention campaigns when you want subscriber win-back beyond email and SMS.',
    longDescription:
      'PostPilot gives subscription brands an offline retention channel through direct mail. With PayWhirl, it can be used for win-back campaigns, reactivation efforts, and lifecycle touches that go beyond email and SMS.',
  },
  ecomposer: {
    name: 'EComposer Landing Page Builder',
    icon: '/images/partners/ecomposer.png',
    url: 'https://apps.shopify.com/ecomposer',
    description:
      'Design flexible storefront pages that present PayWhirl subscriptions in a cleaner, more conversion-focused layout.',
    longDescription:
      'EComposer gives you more flexibility when designing subscription-focused storefront pages. It works well for merchants who want to present PayWhirl plans in a cleaner, more polished layout without relying only on default page structures.',
  },
  lantern: {
    name: 'Lantern AI Quiz Builder',
    icon: '/images/partners/lantern.png',
    url: 'https://apps.shopify.com/lantern-ai-quiz-builder',
    description:
      'Guide shoppers into the right PayWhirl subscription through quizzes that personalize recommendations before checkout.',
    longDescription:
      'Lantern helps you guide shoppers toward the right products and subscription options through interactive quizzes. Used with PayWhirl, it can make subscription offers feel more personalized and easier to understand.',
  },
  rebuy: {
    name: 'Rebuy Personalization Engine',
    icon: '/images/partners/rebuy.png',
    url: 'https://apps.shopify.com/rebuy',
    description:
      'Layer smarter recommendations and upsells around PayWhirl subscriptions to increase conversion and reorder value.',
    longDescription:
      'Rebuy helps you add smarter recommendations, upsells, and merchandising throughout the buying journey. Alongside PayWhirl, it can increase conversion and encourage higher-value subscription orders.',
  },
  'bundler-product-bundles': {
    name: 'Bundler - Product Bundles',
    icon: '/images/partners/bundler-product-bundles.png',
    url: 'https://apps.shopify.com/bundler-product-bundles',
    description:
      'Bundle PayWhirl subscription products with complementary items to raise average order value and plan uptake.',
    longDescription:
      'Bundler helps you package products into offers that make subscriptions more appealing. When combined with PayWhirl, it can raise average order value and make multi-product subscription plans easier to sell.',
  },
  gempages: {
    name: 'GemPages Landing Page Builder',
    icon: '/images/partners/gempages.jpeg',
    url: 'https://apps.shopify.com/gempages',
    description:
      'Launch higher-converting pages that explain PayWhirl subscription benefits more clearly and sell plans more effectively.',
    longDescription:
      'GemPages is a strong option when you want more control over how PayWhirl subscriptions are presented across landing pages and product pages. It can help you explain plan value more clearly and build pages designed to convert.',
  },
  goaffpro: {
    name: 'GOAFFPRO - Affiliate Marketing',
    icon: '/images/partners/goaffpro.png',
    url: 'https://apps.shopify.com/goaffpro',
    description:
      'Reward partners for driving subscription signups and track recurring revenue sourced through affiliates.',
    longDescription:
      'GoAffPro helps you build an affiliate program around recurring revenue. Used with PayWhirl, it can track subscription signups from partners and make it easier to reward the affiliates who drive long-term value.',
  },
  tapfiliate: {
    name: 'Tapfiliate Affiliate Marketing',
    icon: '/images/partners/tapfiliate.png',
    url: 'https://apps.shopify.com/tapfiliate',
    description:
      'Track which affiliates bring in subscribers so you can grow recurring revenue through partner channels.',
    longDescription:
      'Tapfiliate helps you track and reward partners who drive subscription growth. Paired with PayWhirl, it can support recurring commission models and give you better visibility into which affiliates bring in long-term subscribers.',
  },
  'affliate-by-secomapp': {
    name: 'UpPromote Affiliate Marketing',
    icon: '/images/partners/affliate-by-secomapp.png',
    url: 'https://apps.shopify.com/affliate-by-secomapp',
    description:
      'Scale subscription acquisition through influencers and affiliates while keeping attribution tied to PayWhirl signups.',
    longDescription:
      'UpPromote gives you affiliate and influencer tools to scale partner-led acquisition. With PayWhirl, it can help you tie subscription signups back to the right partners and grow recurring revenue more confidently.',
  },
  locksmith: {
    name: 'Locksmith',
    icon: '/images/partners/locksmith.jpeg',
    url: 'https://apps.shopify.com/locksmith',
    description:
      'Lock products or content behind an active PayWhirl subscription to create membership-style access and perks.',
    longDescription:
      'Locksmith helps you turn an active PayWhirl subscription into access. It is a strong fit for membership models where content, products, or special experiences should only be available to current subscribers.',
  },
  customerhub: {
    name: 'C:Hub Customer Accounts',
    icon: '/images/partners/customerhub.png',
    url: 'https://apps.shopify.com/customerhub',
    description:
      'Give subscribers a better self-service account area where PayWhirl-related actions feel more polished and accessible.',
    longDescription:
      'C:Hub can improve the customer account experience around subscriptions by making self-service actions feel cleaner and more accessible. Used with PayWhirl, it helps subscribers manage their relationship with your brand in a more polished environment.',
  },
  helpdesk: {
    name: 'Gorgias: AI, Helpdesk & Chat',
    icon: '/images/partners/helpdesk.png',
    url: 'https://apps.shopify.com/helpdesk',
    description:
      'Connect subscriber support, live chat, and retention workflows so PayWhirl customers get help without friction.',
    longDescription:
      'Gorgias helps you bring customer support, live chat, and automation into one place. When used with PayWhirl, it can make subscription-related support faster and smoother for both your team and your customers.',
  },
  'transcy-multiple-languages': {
    name: 'Transcy: AI Language Translate',
    icon: '/images/partners/transcy-multiple-languages.png',
    url: 'https://apps.shopify.com/transcy-multiple-languages',
    description:
      'Localize the storefront around PayWhirl subscriptions so multilingual shoppers understand plans and pricing more easily.',
    longDescription:
      'Transcy helps you localize the shopping experience across language and currency. For PayWhirl merchants selling internationally, that can make subscription plans easier to understand and more accessible to shoppers in different markets.',
  },
  littledata: {
    name: 'Littledata - The Data Layer',
    icon: '/images/partners/littledata.jpeg',
    url: 'https://apps.shopify.com/littledata',
    description:
      'Send cleaner subscription order data into analytics and ad platforms so PayWhirl revenue is measured more accurately.',
    longDescription:
      'Littledata helps you send cleaner subscription and order data into analytics and advertising platforms. With PayWhirl, that means better visibility into subscription performance, attribution, and marketing impact.',
  },
  shipstation: {
    name: 'ShipStation',
    icon: '/images/partners/shipstation.png',
    url: 'https://apps.shopify.com/shipstation',
    description:
      'Push PayWhirl-driven orders into fulfillment faster so subscription operations stay organized as recurring volume grows.',
    longDescription:
      'ShipStation helps move PayWhirl-driven orders into shipping and fulfillment more efficiently. It is a strong fit for subscription brands that need operations to stay organized as recurring order volume grows.',
  },
  zapier: {
    name: 'Zapier: Workflow Automation',
    icon: '/images/partners/zapier.png',
    url: 'https://apps.shopify.com/zapier',
    description:
      'Use PayWhirl events to automate downstream workflows across your support, CRM, reporting, and operations stack.',
    longDescription:
      'Zapier helps you turn PayWhirl events into automated workflows across the rest of your stack. It is especially useful when you want subscription activity to trigger updates in support tools, CRMs, reporting systems, or internal processes.',
  },
}

export type PartnerAppKey = keyof typeof partnerApps

export interface PartnerSection {
  label: string
  title: string
  description: string
  featured?: boolean
  apps: PartnerAppKey[]
}

export const partnerSections: PartnerSection[] = [
  {
    label: 'FEATURED',
    title: 'Top PayWhirl pairings',
    description:
      'Start with the tools merchants often pair with PayWhirl to improve retention, lift conversion, and create better subscription experiences.',
    featured: true,
    apps: ['glow-loyalty', 'upsell-wizard', 'klaviyo', 'pagefly'],
  },
  {
    label: 'CATEGORY',
    title: 'Integration Partners',
    description:
      'Work with specialists who can tailor PayWhirl and Shopify to your business, from implementation and theme updates to custom workflows and growth strategy.',
    apps: ['storetasker', 'brightside', 'shopify-experts', 'phoenix-industries'],
  },
  {
    label: 'CATEGORY',
    title: 'Loyalty and retention',
    description:
      'Keep subscribers engaged with rewards, referrals, gifting, and repeat-purchase programs.',
    apps: [
      'glow-loyalty',
      'givy',
      'smile-io',
      'product-reviews-addon',
      'loyaltylion',
      'growave',
      'govalo',
    ],
  },
  {
    label: 'CATEGORY',
    title: 'Marketing and messaging',
    description:
      'Drive subscriber acquisition, renewal, win-back, and lifecycle messaging across email, SMS, and direct mail.',
    apps: [
      'klaviyo',
      'omnisend',
      'mailchimp',
      'attentive',
      'postscript-sms-marketing',
      'marsello',
      'recart',
      'postpilot',
    ],
  },
  {
    label: 'CATEGORY',
    title: 'Storefront and conversion',
    description:
      'Improve how PayWhirl offers appear on product pages, landing pages, quizzes, bundles, and upsells.',
    apps: [
      'upsell-wizard',
      'pagefly',
      'ecomposer',
      'lantern',
      'rebuy',
      'bundler-product-bundles',
      'gempages',
    ],
  },
  {
    label: 'CATEGORY',
    title: 'Affiliate and referral growth',
    description: 'Track who drives subscription revenue and scale partner-led acquisition.',
    apps: ['goaffpro', 'tapfiliate', 'affliate-by-secomapp'],
  },
  {
    label: 'CATEGORY',
    title: 'Customer experience and access',
    description:
      'Give subscribers better account tools, support, gated access, and localized experiences.',
    apps: ['locksmith', 'customerhub', 'helpdesk', 'transcy-multiple-languages'],
  },
  {
    label: 'CATEGORY',
    title: 'Operations and analytics',
    description: 'Connect subscriptions to fulfillment, measurement, and automation workflows.',
    apps: ['littledata', 'shipstation', 'zapier'],
  },
]
